import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Typography,
  Stack,
  Chip,
  Skeleton,
  alpha,
  Autocomplete,
  TextField,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Filters from '../../components/Filters/Filters';
import DashboardsMenu from '../../components/DashboardsMenu/DashboardsMenu';
import { VocuherCategory } from '../../utils/common-utils';
import Chart from 'react-apexcharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBalanceSheet, fetchProfitLoss } from '../../store/reportStore/reportThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ACCENT = '#047857';
const ACCENT_LIGHT = '#d9f99d';
const ACCENT_SOFT = '#ecfccb';
const BG = '#fafafb';
const SURFACE = '#ffffff';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#cbd5e1';

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: 'none',
  backgroundColor: SURFACE,
};

// Compact currency format — Cr / L / K with rounding — used in graphs.
// Small amounts stay as plain rupees instead of "0.04 L".
const fmtShort = (num: number): string => Utils.formatCompact(num);

// Full Indian-format currency — used in KPI cards
const fmtFull = (num: number): string => Utils.formatSmart(num);

const topN = (groups: any[] | undefined, n = 5) => {
  if (!groups || !groups.length) return { names: [], values: [] };
  const sorted = [...groups].sort((a, b) => (b.total || 0) - (a.total || 0)).slice(0, n);
  return {
    names: sorted.map((g) => g.name || g.accountingCode || '—'),
    values: sorted.map((g) => Number(g.total || 0)),
  };
};

const CoaDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector((s) => s.auth);
  const accountId = selectedAccount?._id;
  const { balanceSheetData, profitLossData, status, errorMessage } = useAppSelector((s) => s.reports);

  const [isLoading, setIsLoading] = useState(false);
  const [filterParams, setFilterParams] = useState('');
  const [selectedLedgerKey, setSelectedLedgerKey] = useState<string | null>(null);
  // P&L Flow bar click → hold the clicked bar's ledger breakdown in a dialog
  // (the tooltip only shows on hover and vanishes on mouse-out).
  const [plDialog, setPlDialog] = useState<{ title: string; total: number; color: string; groups: any[] | undefined; x: number; y: number } | null>(null);
  // BS Composition donut: same click-to-hold behaviour as the P&L bar.
  const [bsDialog, setBsDialog] = useState<{ title: string; total: number; color: string; groups: any[] | undefined; x: number; y: number } | null>(null);
  // Revenue & Expenses bar: click-to-hold breakdown for the clicked group.
  const [reDialog, setReDialog] = useState<{ title: string; total: number; color: string; groups: any[] | undefined; x: number; y: number } | null>(null);
  // Assets Breakdown donut: click-to-hold breakdown for the clicked group.
  const [assetsDialog, setAssetsDialog] = useState<{ title: string; total: number; color: string; groups: any[] | undefined; x: number; y: number } | null>(null);
  // Liabilities Breakdown donut: click-to-hold breakdown for the clicked group.
  const [liabDialog, setLiabDialog] = useState<{ title: string; total: number; color: string; groups: any[] | undefined; x: number; y: number } | null>(null);

  const handleFilterChange = (params: string) => {
    setFilterParams(params);
  };

  useEffect(() => {
    if (!accountId || !filterParams) return;
    setIsLoading(true);
    Promise.all([
      dispatch(fetchBalanceSheet({ accountId, startDate: '', endDate: '', params: filterParams })),
      dispatch(fetchProfitLoss({ accountId, startDate: '', endDate: '', params: filterParams })),
    ]).finally(() => setIsLoading(false));
  }, [accountId, filterParams, dispatch]);

  const pl: any = profitLossData || {};
  const bs: any = balanceSheetData || {};

  const totalRevenue = Number(pl?.summary?.totalRevenue ?? pl?.revenue?.totalRevenue ?? 0);
  const totalCOGS = Number(pl?.summary?.totalCOGS ?? pl?.cogs?.total ?? 0);
  const grossProfit = Number(pl?.summary?.grossProfit ?? pl?.grossProfit ?? 0);
  const totalOpEx = Number(pl?.summary?.totalOperatingExpenses ?? pl?.operatingExpenses?.total ?? 0);
  const opProfit = Number(pl?.summary?.operatingProfit ?? pl?.operatingProfit ?? 0);
  const totalOtherEx = Number(pl?.summary?.totalOtherExpenses ?? pl?.otherExpenses?.total ?? 0);
  const netProfit = Number(pl?.summary?.netProfit ?? pl?.netProfitAfterTax ?? 0);
  const netMargin = Number(pl?.summary?.netProfitMargin || 0);
  const totalExpenses = totalCOGS + totalOpEx + totalOtherEx;

  const totalAssets = Number(bs?.assets?.total || 0);
  const totalLiabilities = Number(bs?.liabilities?.total || 0);
  const totalEquity = Number(bs?.equity?.total || 0);
  const isBalanced = bs?.balanceCheck?.isBalanced;
  const balanceDiff = Number(bs?.balanceCheck?.difference || 0);


  const loading = isLoading || status === ApiStatus.LOADING;

  // Flatten group → ledger rows (handles subGroups + direct ledgers)
  const flattenLedgers = (groups: any[] | undefined): any[] => {
    const out: any[] = [];
    (groups || []).forEach((g) => {
      const groupName = g.name || g.accountingCode || '—';
      (g.subGroups || []).forEach((sg: any) => {
        (sg.ledgers || []).forEach((l: any) => {
          out.push({
            group: groupName,
            name: l.name || '—',
            code: l.accountingCode || '',
            debit: Number(l.debit || 0),
            credit: Number(l.credit || 0),
            balance: Number(l.balance ?? l.amount ?? 0),
          });
        });
      });
      (g.ledgers || []).forEach((l: any) => {
        out.push({
          group: groupName,
          name: l.name || '—',
          code: l.accountingCode || '',
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          balance: Number(l.balance ?? l.amount ?? 0),
        });
      });
    });
    return out;
  };

  const MetricCard = ({
    title,
    value,
    sub,
    icon,
    accent = ACCENT,
    highlight = false,
  }: {
    title: string;
    value: string | number;
    sub?: { positive: boolean; label: string } | null;
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        borderColor: highlight ? ACCENT_LIGHT : BORDER,
        height: '100%',
        minHeight: 110,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.12)',
        },
      }}
    >
      <CardContent
        sx={{
          p: 1.75,
          '&:last-child': { pb: 1.75 },
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3, mb: 0.75 }} noWrap>
              {title}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: '1rem' }} noWrap>
              {loading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: highlight ? '#ffffff' : alpha(accent, 0.12),
              color: accent,
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
        <Box sx={{ height: 20, mt: 1, display: 'flex', alignItems: 'center' }}>
          {sub ? (
            <Chip
              size="small"
              icon={sub.positive
                ? <TrendingUpIcon sx={{ fontSize: 11, color: '#16a34a !important' }} />
                : <TrendingDownIcon sx={{ fontSize: 11, color: '#dc2626 !important' }} />}
              label={sub.label}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 600,
                color: sub.positive ? '#16a34a' : '#dc2626',
                backgroundColor: sub.positive ? '#dcfce7' : '#fee2e2',
                '& .MuiChip-icon': { ml: '3px', mr: '-2px' },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          ) : (
            <Typography sx={{ color: MUTED, fontSize: '0.62rem', fontStyle: 'italic' }}>—</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const baseAxis = {
    chart: { toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: { y: { formatter: (v: number) => fmtShort(v) } },
  };

  const plFullNames = [
    'Total Revenue',
    'Cost of Goods Sold',
    'Gross Profit',
    'Operating Expenses',
    'Operating Profit',
    'Other Expenses',
    'Net Profit',
  ];

  // Build vertical ledger-breakdown HTML for any group list
  const TT_FONT = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  const buildLedgerListTooltip = (title: string, totalVal: number, color: string, groups: any[] | undefined) => {
    const ledgers = flattenLedgers(groups);
    const rows = ledgers.map((l) => `
      <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;border-bottom:1px dashed #eef0f3;font-family:${TT_FONT};">
        <div style="flex:1;min-width:0;">
          <div style="font-size:11px;font-weight:600;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-family:${TT_FONT};">${l.name}</div>
          ${l.group ? `<div style="font-size:9px;color:#64748b;font-family:${TT_FONT};">${l.group}</div>` : ''}
        </div>
        <div style="text-align:right;margin-left:10px;min-width:90px;">
          <div style="font-size:11px;font-weight:700;color:${color};font-family:${TT_FONT};">${fmtShort(l.balance || l.debit || l.credit || 0)}</div>
          ${l.debit > 0 || l.credit > 0 ? `<div style="font-size:9px;color:#64748b;font-family:${TT_FONT};">Dr ${fmtShort(l.debit)} · Cr ${fmtShort(l.credit)}</div>` : ''}
        </div>
      </div>`).join('');
    return `
      <div style="padding:10px 12px;font-family:${TT_FONT};background:#fff;min-width:260px;max-width:380px;max-height:240px;overflow-y:auto;border-radius:8px;box-shadow:0 4px 14px rgba(0,0,0,0.08);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;border-bottom:2px solid ${color};padding-bottom:6px;font-family:${TT_FONT};position:sticky;top:0;background:#fff;">
          <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;"></span>
          <b style="color:#0f172a;font-size:12px;flex:1;font-family:${TT_FONT};">${title}</b>
          <span style="color:${color};font-weight:800;font-size:12px;font-family:${TT_FONT};">${fmtShort(totalVal)}</span>
        </div>
        ${ledgers.length === 0 ? `<div style="font-size:10px;color:#64748b;padding:4px 0;font-family:${TT_FONT};">No ledger detail</div>` : rows}
      </div>`;
  };

  // Map P&L Flow bar index → source group list for ledger drilldown
  const plBucketGroups = (idx: number): { groups: any[] | undefined; title: string; computed?: boolean } => {
    switch (idx) {
      case 0: return { groups: [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])], title: 'Total Revenue' };
      case 1: return { groups: pl?.cogs?.breakdown, title: 'Cost of Goods Sold' };
      case 2: return { groups: undefined, title: 'Gross Profit (Revenue − COGS)', computed: true };
      case 3: return { groups: pl?.operatingExpenses?.breakdown, title: 'Operating Expenses' };
      case 4: return { groups: undefined, title: 'Operating Profit (Gross − OpEx)', computed: true };
      case 5: return { groups: pl?.otherExpenses?.breakdown, title: 'Other Expenses' };
      case 6: return { groups: undefined, title: 'Net Profit (Op − Other + Round Off)', computed: true };
      default: return { groups: undefined, title: '' };
    }
  };

  const plBarColors = [ACCENT, '#ef4444', '#3b82f6', '#ef4444', '#3b82f6', '#ef4444', netProfit >= 0 ? ACCENT : '#ef4444'];

  const plBarOptions = useMemo(
    () => ({
      ...baseAxis,
      chart: {
        ...baseAxis.chart,
        type: 'bar' as const,
        height: 260,
        events: {
          // Click a bar → freeze its ledger breakdown next to THAT bar.
          dataPointSelection: (e: any, _ctx: any, cfg: any) => {
            const idx = cfg?.dataPointIndex;
            if (idx === undefined || idx < 0) return;
            const bucket = plBucketGroups(idx);
            const val = plBarSeries?.[0]?.data?.[idx] || 0;
            const color = plBarColors?.[idx] || TEXT;
            // Position the frozen panel at the clicked bar. Convert the click's
            // page coords into coords relative to the chart's Paper container.
            let x = 0, y = 0;
            const paper = (e?.target as HTMLElement)?.closest('.MuiPaper-root') as HTMLElement | null;
            if (paper && e) {
              const r = paper.getBoundingClientRect();
              x = (e.clientX ?? r.left) - r.left;
              y = (e.clientY ?? r.top) - r.top;
            }
            setPlDialog({ title: bucket.title, total: val, color, groups: bucket.groups, x, y });
          },
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 6,
          borderRadiusApplication: 'end' as const,
          distributed: true,
          dataLabels: { position: 'top' as const },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (v: number) => fmtShort(v),
        offsetY: -20,
        style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      },
      xaxis: {
        categories: ['Revenue', 'COGS', 'Gross', 'Op Exp', 'Op Profit', 'Other Exp', 'Net'],
        labels: { style: { colors: MUTED, fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { colors: MUTED, fontSize: '11px' }, formatter: (v: number) => fmtShort(v) } },
      colors: plBarColors,
      tooltip: {
        // When a bar is clicked (frozen panel open), suppress the hover tooltip
        // so we don't show the same breakdown twice.
        enabled: !plDialog,
        custom: ({ dataPointIndex, w }: any) => {
          const val = w?.config?.series?.[0]?.data?.[dataPointIndex] || 0;
          const color = w?.config?.colors?.[dataPointIndex] || TEXT;
          const meta = plBucketGroups(dataPointIndex);
          if (meta.computed) {
            return `<div style="padding:8px 12px;font-size:11px;background:#fff;border-radius:8px;min-width:200px;font-family:${TT_FONT};box-shadow:0 4px 14px rgba(0,0,0,0.08);">
              <b style="color:${TEXT};font-size:12px;font-family:${TT_FONT};">${meta.title}</b><br/>
              <span style="color:${color};font-weight:800;font-size:13px;font-family:${TT_FONT};">${fmtShort(val)}</span>
              <div style="font-size:9px;color:#64748b;margin-top:4px;font-style:italic;font-family:${TT_FONT};">Derived value — no direct ledger</div>
            </div>`;
          }
          return buildLedgerListTooltip(meta.title || plFullNames[dataPointIndex] || '', val, color, meta.groups);
        },
      },
    }),
    [netProfit, plDialog]
  );

  const plBarSeries = [{ name: 'Amount', data: [totalRevenue, totalCOGS, grossProfit, totalOpEx, opProfit, totalOtherEx, netProfit] }];

  const bsDonutColors = [ACCENT, '#ef4444', '#3b82f6'];
  const bsDonutOptions = {
    chart: {
      type: 'donut' as const,
      fontFamily: 'inherit',
      events: {
        // Click a slice → freeze that section's ledger-group breakdown.
        dataPointSelection: (e: any, _ctx: any, cfg: any) => {
          const i = cfg?.dataPointIndex ?? cfg?.seriesIndex;
          if (i === undefined || i < 0) return;
          const sections = [
            { title: 'Assets', total: totalAssets, groups: bs?.assets?.groups },
            { title: 'Liabilities', total: totalLiabilities, groups: bs?.liabilities?.groups },
            { title: 'Equity', total: totalEquity, groups: bs?.equity?.groups },
          ];
          const s = sections[i];
          if (!s) return;
          let x = 0, y = 0;
          const paper = (e?.target as HTMLElement)?.closest('.MuiPaper-root') as HTMLElement | null;
          if (paper && e) {
            const r = paper.getBoundingClientRect();
            x = (e.clientX ?? r.left) - r.left;
            y = (e.clientY ?? r.top) - r.top;
          }
          setBsDialog({ title: s.title, total: s.total, color: bsDonutColors[i], groups: s.groups, x, y });
        },
      },
    },
    labels: ['Assets', 'Liabilities', 'Equity'],
    colors: bsDonutColors,
    legend: { position: 'bottom' as const, fontSize: '11px', labels: { colors: MUTED }, markers: { width: 8, height: 8, radius: 12 } as any, itemMargin: { horizontal: 6, vertical: 2 } },
    dataLabels: { enabled: true, style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] }, formatter: (val: number) => `${val.toFixed(0)}%` },
    stroke: { width: 2, colors: ['#fff'] },
    plotOptions: { pie: { donut: { size: '60%', labels: { show: false } } } },
    tooltip: { enabled: !bsDialog, y: { formatter: (v: number) => fmtShort(v) } },
  };
  const bsDonutSeries = [totalAssets, totalLiabilities, totalEquity];

  const revenueTop = topN(pl?.revenue?.salesIncome?.breakdown);
  const expenseTop = topN(pl?.operatingExpenses?.breakdown);

  const horizBar = (categories: string[], color: string) => ({
    ...baseAxis,
    chart: { ...baseAxis.chart, type: 'bar' as const },
    plotOptions: {
      bar: { horizontal: true, barHeight: '60%', borderRadius: 4, borderRadiusApplication: 'end' as const, distributed: false, dataLabels: { position: 'top' as const } },
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'start' as const,
      formatter: (v: number) => fmtShort(v),
      style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      offsetX: 8,
    },
    xaxis: { categories, labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: any) => fmtShort(Number(v)) }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { style: { colors: TEXT, fontSize: '10px' } } },
    colors: [color],
  });

  const heatmapOpts = (color: string, maxVal: number, names: string[]) => ({
    chart: { type: 'heatmap' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: {
      enabled: true,
      formatter: (val: number, opt: any) => {
        const name = names[opt.dataPointIndex] || '';
        return [name, fmtShort(val)] as any;
      },
      style: { fontSize: '12px', fontWeight: 700, colors: ['#fff'] },
      offsetY: -2,
    },
    stroke: { width: 3, colors: ['#fff'] },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { show: false, labels: { show: false } },
    grid: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    legend: { show: false },
    plotOptions: {
      heatmap: {
        radius: 8,
        enableShades: false,
        colorScale: {
          ranges: [
            { from: 0, to: maxVal * 0.25, name: 'Low', color: color + '55' },
            { from: maxVal * 0.25 + 0.01, to: maxVal * 0.5, name: 'Mid-Low', color: color + '88' },
            { from: maxVal * 0.5 + 0.01, to: maxVal * 0.75, name: 'Mid-High', color: color + 'bb' },
            { from: maxVal * 0.75 + 0.01, to: maxVal, name: 'High', color },
          ],
        },
      },
    },
    tooltip: {
      y: { formatter: (v: number) => fmtShort(v) },
      x: { formatter: (_v: any, opt: any) => names[opt?.dataPointIndex] || '' },
    },
  });

  const heatmapSeries = (names: string[], values: number[]) => [{
    name: 'Amount',
    data: names.map((n, i) => ({ x: n, y: values[i] || 0 })),
  }];

  const bubbleOpts = (categories: string[], palette: string[]) => ({
    chart: { type: 'bubble' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    dataLabels: {
      enabled: true,
      formatter: (_val: any, opt: any) => categories[opt.seriesIndex] || '',
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    xaxis: {
      type: 'category' as const,
      categories,
      labels: { style: { colors: MUTED, fontSize: '10px' }, rotate: -20, trim: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickPlacement: 'on',
    },
    yaxis: {
      labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: number) => fmtShort(v) },
    },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    legend: { show: false },
    fill: { opacity: 0.85 },
    colors: palette,
    plotOptions: { bubble: { minBubbleRadius: 18, maxBubbleRadius: 60 } },
    tooltip: {
      custom: ({ seriesIndex, w }: any) => {
        const name = w?.config?.series?.[seriesIndex]?.name || categories[seriesIndex];
        const pt = w?.config?.series?.[seriesIndex]?.data?.[0];
        const val = Array.isArray(pt) ? pt[2] : 0;
        return `<div style="padding:6px 10px;font-size:11px;"><b>${name}</b><br/>${fmtShort(val)}</div>`;
      },
    },
  });

  const bubbleSeries = (names: string[], values: number[]) =>
    names.map((n, i) => ({ name: n, data: [[i, values[i] || 0, values[i] || 0]] }));

  const areaSplineOpts = (categories: string[], color: string) => ({
    chart: { type: 'area' as const, toolbar: { show: false }, fontFamily: 'inherit', sparkline: { enabled: false } },
    stroke: { curve: 'smooth' as const, width: 3, colors: [color] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color, opacity: 0.45 },
          { offset: 100, color, opacity: 0.02 },
        ],
      },
    },
    markers: { size: 5, colors: [color], strokeColors: '#fff', strokeWidth: 2, hover: { size: 7 } },
    dataLabels: {
      enabled: true,
      formatter: (v: number) => fmtShort(v),
      offsetY: -8,
      style: { fontSize: '10px', fontWeight: 600, colors: [TEXT] },
      background: { enabled: false },
    },
    xaxis: {
      categories,
      labels: { style: { colors: MUTED, fontSize: '10px' }, rotate: -20, trim: true, hideOverlappingLabels: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: MUTED, fontSize: '10px' }, formatter: (v: number) => fmtShort(v) } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } } },
    legend: { show: false },
    tooltip: { y: { formatter: (v: number) => fmtShort(v) } },
    colors: [color],
  });

  const stackedHorizOpts = (names: string[], palette: string[]) => ({
    chart: { type: 'bar' as const, stacked: true, stackType: '100%' as const, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'around' as const,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(0)}%`,
      style: { fontSize: '10px', fontWeight: 700, colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { show: false } },
    grid: { show: false, padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    legend: {
      position: 'bottom' as const,
      fontSize: '11px',
      labels: { colors: MUTED },
      markers: { width: 10, height: 10, radius: 12 } as any,
      itemMargin: { horizontal: 6, vertical: 2 },
    },
    colors: palette,
    tooltip: {
      y: {
        formatter: (val: number, opts: any) => {
          const raw = opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[0] || 0;
          return fmtShort(raw);
        },
      },
    },
    series: names,
  });

  const stackedHorizSeries = (names: string[], values: number[]) =>
    names.map((n, i) => ({ name: n, data: [values[i] || 0] }));

  const radialOpts = (labels: string[], palette: string[], total: number) => ({
    chart: { type: 'radialBar' as const, fontFamily: 'inherit', toolbar: { show: false } },
    labels,
    colors: palette,
    plotOptions: {
      radialBar: {
        offsetY: 0,
        startAngle: 0,
        endAngle: 270,
        hollow: { margin: 5, size: '38%', background: 'transparent' },
        track: { background: '#f1f5f9', strokeWidth: '100%', margin: 4 },
        dataLabels: {
          name: { show: true, fontSize: '11px', fontWeight: 600, color: MUTED, offsetY: -4 },
          value: {
            show: true,
            fontSize: '14px',
            fontWeight: 700,
            color: TEXT,
            offsetY: 4,
            formatter: () => fmtShort(total),
          },
          total: {
            show: true,
            label: 'Total',
            fontSize: '10px',
            fontWeight: 600,
            color: MUTED,
            formatter: () => fmtShort(total),
          },
        },
      },
    },
    stroke: { lineCap: 'round' as const },
    legend: {
      show: true,
      position: 'bottom' as const,
      fontSize: '10px',
      labels: { colors: MUTED },
      markers: { width: 8, height: 8, radius: 12 } as any,
      itemMargin: { horizontal: 4, vertical: 1 },
      formatter: (seriesName: string, opts: any) => {
        const val = opts?.w?.globals?.seriesTotals?.[opts.seriesIndex] || 0;
        return `${seriesName}: ${fmtShort((val / 100) * total)}`;
      },
    },
    tooltip: {
      y: { formatter: (v: number) => `${v.toFixed(1)}%` },
    },
  });

  const radialSeries = (values: number[], total: number) =>
    total > 0 ? values.map((v) => Number(((v / total) * 100).toFixed(2))) : values.map(() => 0);

  const revenuePalette = [ACCENT, '#22c55e', '#10b981', '#06b6d4', '#3b82f6'];
  const expensePalette = ['#ef4444', '#f97316', '#f59e0b', '#dc2626', '#b91c1c'];

  // Combined Rev/Exp — flatten into single series, color-code by type
  const combinedCats = [...revenueTop.names, ...expenseTop.names];
  const combinedValues = [...revenueTop.values, ...expenseTop.values];
  const combinedColors = [
    ...revenueTop.names.map(() => ACCENT),
    ...expenseTop.names.map(() => '#ef4444'),
  ];

  const combinedBarOpts = {
    chart: {
      type: 'bar' as const,
      toolbar: { show: false },
      fontFamily: 'inherit',
      events: {
        // Click a bar → freeze that group's ledger breakdown next to it.
        dataPointSelection: (e: any, _ctx: any, cfg: any) => {
          const idx = cfg?.dataPointIndex ?? cfg?.seriesIndex;
          if (idx === undefined || idx < 0) return;
          const name = combinedCats[idx];
          const total = combinedValues[idx] || 0;
          const isRev = idx < revenueTop.names.length;
          const color = isRev ? ACCENT : '#ef4444';
          const allGroups = isRev
            ? [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])]
            : (pl?.operatingExpenses?.breakdown || []);
          const matchingGroup = allGroups.find((g: any) => (g.name || g.accountingCode) === name);
          let x = 0, y = 0;
          const paper = (e?.target as HTMLElement)?.closest('.MuiPaper-root') as HTMLElement | null;
          if (paper && e) {
            const r = paper.getBoundingClientRect();
            x = (e.clientX ?? r.left) - r.left;
            y = (e.clientY ?? r.top) - r.top;
          }
          setReDialog({ title: `${name} · ${isRev ? 'Revenue' : 'Expense'}`, total, color, groups: matchingGroup ? [matchingGroup] : [], x, y });
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        dataLabels: { position: 'top' as const },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: 'start' as const,
      offsetX: 40,
      formatter: (v: number) => `  ${fmtShort(v)}`,
      style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] },
      background: { enabled: false },
      dropShadow: { enabled: false },
    },
    xaxis: {
      categories: combinedCats,
      min: 0,
      max: Math.ceil((Math.max(...combinedValues, 1) * 1.25) / 1000) * 1000,
      tickAmount: 7,
      labels: {
        style: { colors: MUTED, fontSize: '10px' },
        formatter: (v: any) => fmtShort(Math.round(Number(v))),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: TEXT, fontSize: '11px', fontWeight: 600 } } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    colors: combinedColors,
    fill: { opacity: 1 },
    tooltip: {
      enabled: !reDialog,
      y: { formatter: (v: number) => fmtShort(v) },
      custom: ({ dataPointIndex, w }: any) => {
        const name = w?.globals?.labels?.[dataPointIndex] || '';
        const val = w?.config?.series?.[0]?.data?.[dataPointIndex] || 0;
        const isRev = dataPointIndex < revenueTop.names.length;
        const color = isRev ? ACCENT : '#ef4444';
        // Find matching group in P&L breakdown
        const allGroups = isRev
          ? [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])]
          : (pl?.operatingExpenses?.breakdown || []);
        const matchingGroup = allGroups.find((g: any) => (g.name || g.accountingCode) === name);
        return buildLedgerListTooltip(`${name} · ${isRev ? 'Revenue' : 'Expense'}`, val, color, matchingGroup ? [matchingGroup] : []);
      },
    },
  };

  const combinedBarSeries = [{ name: 'Amount', data: combinedValues }];

  const hasCombinedData = combinedCats.length > 0;

  const revenueTopTotal = revenueTop.values.reduce((s, v) => s + v, 0);
  const expenseTopTotal = expenseTop.values.reduce((s, v) => s + v, 0);

  const assetsGroupsBreak = topN(bs?.assets?.groups, 6);
  const liabGroupsBreak = topN(bs?.liabilities?.groups, 6);

  const palette = [ACCENT, '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

  // Build ledger tooltip HTML per group for donut hover
  const buildGroupTooltip = (groupName: string, groups: any[] | undefined, color: string, total: number) => {
    const g = (groups || []).find((x: any) => (x.name || x.accountingCode) === groupName);
    if (!g) return `<div style="padding:12px 14px;font-size:13px;font-family:${TT_FONT};"><b>${groupName}</b><br/>${fmtShort(total)}</div>`;
    return buildLedgerListTooltip(groupName, Number(g.total || total), color, [g]);
  };

  const assetsDonutOptions = {
    chart: {
      type: 'donut' as const,
      fontFamily: 'inherit',
      events: {
        // Click a slice → freeze that asset group's ledger breakdown.
        dataPointSelection: (e: any, _ctx: any, cfg: any) => {
          const idx = cfg?.dataPointIndex ?? cfg?.seriesIndex;
          if (idx === undefined || idx < 0) return;
          const groupName = assetsGroupsBreak.names[idx];
          const color = palette[idx % palette.length];
          const total = assetsGroupsBreak.values[idx] || 0;
          const g = (bs?.assets?.groups || []).find((x: any) => (x.name || x.accountingCode) === groupName);
          let x = 0, y = 0;
          const paper = (e?.target as HTMLElement)?.closest('.MuiPaper-root') as HTMLElement | null;
          if (paper && e) {
            const r = paper.getBoundingClientRect();
            x = (e.clientX ?? r.left) - r.left;
            y = (e.clientY ?? r.top) - r.top;
          }
          setAssetsDialog({ title: groupName, total, color, groups: g ? [g] : [], x, y });
        },
      },
    },
    labels: assetsGroupsBreak.names,
    colors: palette,
    legend: { position: 'bottom' as const, fontSize: '10px', labels: { colors: MUTED }, markers: { width: 8, height: 8, radius: 12 } as any, itemMargin: { horizontal: 4, vertical: 1 } },
    dataLabels: { enabled: true, style: { fontSize: '10px', fontWeight: 700, colors: ['#fff'] }, formatter: (val: number) => `${val.toFixed(0)}%` },
    stroke: { width: 2, colors: ['#fff'] },
    plotOptions: { pie: { donut: { size: '58%', labels: { show: false } } } },
    tooltip: {
      enabled: !assetsDialog,
      custom: ({ seriesIndex, w }: any) => {
        const groupName = w?.config?.labels?.[seriesIndex] || '';
        const color = w?.config?.colors?.[seriesIndex] || '#3b82f6';
        const value = w?.config?.series?.[seriesIndex] || 0;
        return buildGroupTooltip(groupName, bs?.assets?.groups, color, value);
      },
    },
  };
  const liabDonutColors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', ACCENT, '#06b6d4'];
  const liabDonutOptions = {
    ...assetsDonutOptions,
    // Own chart object so the spread's assets-bound events handler is NOT inherited.
    chart: {
      type: 'donut' as const,
      fontFamily: 'inherit',
      events: {
        // Click a slice → freeze that liability group's ledger breakdown.
        dataPointSelection: (e: any, _ctx: any, cfg: any) => {
          const idx = cfg?.dataPointIndex ?? cfg?.seriesIndex;
          if (idx === undefined || idx < 0) return;
          const groupName = liabGroupsBreak.names[idx];
          const color = liabDonutColors[idx % liabDonutColors.length];
          const total = liabGroupsBreak.values[idx] || 0;
          const g = (bs?.liabilities?.groups || []).find((x: any) => (x.name || x.accountingCode) === groupName);
          let x = 0, y = 0;
          const paper = (e?.target as HTMLElement)?.closest('.MuiPaper-root') as HTMLElement | null;
          if (paper && e) {
            const r = paper.getBoundingClientRect();
            x = (e.clientX ?? r.left) - r.left;
            y = (e.clientY ?? r.top) - r.top;
          }
          setLiabDialog({ title: groupName, total, color, groups: g ? [g] : [], x, y });
        },
      },
    },
    labels: liabGroupsBreak.names,
    colors: liabDonutColors,
    tooltip: {
      enabled: !liabDialog,
      custom: ({ seriesIndex, w }: any) => {
        const groupName = w?.config?.labels?.[seriesIndex] || '';
        const color = w?.config?.colors?.[seriesIndex] || '#ef4444';
        const value = w?.config?.series?.[seriesIndex] || 0;
        return buildGroupTooltip(groupName, bs?.liabilities?.groups, color, value);
      },
    },
  };

  // Combine every ledger from BS + P&L into a single dropdown source
  const allLedgers = useMemo(() => {
    const sections: { section: string; groups: any[] | undefined }[] = [
      { section: 'Assets', groups: bs?.assets?.groups },
      { section: 'Liabilities', groups: bs?.liabilities?.groups },
      { section: 'Equity', groups: bs?.equity?.groups },
      { section: 'Revenue', groups: [...(pl?.revenue?.salesIncome?.breakdown || []), ...(pl?.revenue?.otherIncome?.breakdown || [])] },
      { section: 'COGS', groups: pl?.cogs?.breakdown },
      { section: 'Operating Expenses', groups: pl?.operatingExpenses?.breakdown },
      { section: 'Other Expenses', groups: pl?.otherExpenses?.breakdown },
    ];
    const seen = new Set<string>();
    const out: { key: string; section: string; group: string; name: string; code: string; debit: number; credit: number; balance: number }[] = [];
    sections.forEach(({ section, groups }) => {
      flattenLedgers(groups).forEach((l) => {
        const key = `${section}::${l.group}::${l.name}::${l.code}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ key, section, ...l });
      });
    });
    return out.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
  }, [bs, pl]);

  useEffect(() => {
    if (!selectedLedgerKey && allLedgers.length > 0) {
      setSelectedLedgerKey(allLedgers[0].key);
    }
    if (selectedLedgerKey && allLedgers.length > 0 && !allLedgers.find((l) => l.key === selectedLedgerKey)) {
      setSelectedLedgerKey(allLedgers[0].key);
    }
  }, [allLedgers, selectedLedgerKey]);

  const selectedLedger = useMemo(
    () => allLedgers.find((l) => l.key === selectedLedgerKey) || null,
    [allLedgers, selectedLedgerKey]
  );

  const ledgerBarOptions = useMemo(
    () => ({
      ...baseAxis,
      chart: { ...baseAxis.chart, type: 'bar' as const, height: 260 },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 8,
          borderRadiusApplication: 'end' as const,
          distributed: true,
          dataLabels: { position: 'top' as const },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (v: number) => fmtShort(v),
        offsetY: -22,
        style: { fontSize: '11px', fontWeight: 700, colors: [TEXT] },
      },
      xaxis: {
        categories: ['Debit', 'Credit', 'Balance'],
        labels: { style: { colors: MUTED, fontSize: '11px', fontWeight: 600 } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { style: { colors: MUTED, fontSize: '11px' }, formatter: (v: number) => fmtShort(v) },
        forceNiceScale: true,
        max: (max: number) => (max > 0 ? Math.ceil((max * 1.25) / 100) * 100 : 100),
      },
      colors: ['#3b82f6', '#f59e0b', (selectedLedger?.balance ?? 0) >= 0 ? ACCENT : '#ef4444'],
      tooltip: { y: { formatter: (v: number) => fmtShort(v) } },
    }),
    [selectedLedger]
  );

  const ledgerBarSeries = useMemo(
    () => [
      {
        name: selectedLedger?.name || 'Ledger',
        data: [
          Number(selectedLedger?.debit || 0),
          Number(selectedLedger?.credit || 0),
          Number(selectedLedger?.balance || 0),
        ],
      },
    ],
    [selectedLedger]
  );

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Financial Dashboard
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>
            {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · {selectedAccount?.name || 'your account'}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="flex-start" sx={{ gap: 1.5, width: { xs: '100%', md: 'auto' }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          {/* Balance check pill — removed */}
          <IconButton
            onClick={() => {
              if (accountId && filterParams) {
                setIsLoading(true);
                Promise.all([
                  dispatch(fetchBalanceSheet({ accountId, startDate: '', endDate: '', params: filterParams })),
                  dispatch(fetchProfitLoss({ accountId, startDate: '', endDate: '', params: filterParams })),
                ]).finally(() => setIsLoading(false));
              }
            }}
            disabled={isLoading}
            sx={{
              width: 38,
              height: 38,
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              flexShrink: 0,
              '&:hover': { backgroundColor: '#eff6ff', borderColor: '#bfdbfe' }
            }}
          >
            <RefreshIcon sx={{ fontSize: 22, color: '#2563eb' }} />
          </IconButton>
          <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 350 }, '& .c-text-field-3': { marginLeft: '0 !important' },
            '& > div > .MuiInputBase-root, & > div > .MuiAutocomplete-root': { maxWidth: { md: '170px !important' } },
            '& .MuiTextField-root': { minWidth: { md: '145px !important' }, maxWidth: { md: '170px !important' } },
            // Custom Date pickers drop to their own line under Date/Location, right-aligned, instead of stretching the row
            '& .filters-custom-dates': { flexBasis: '100%', justifyContent: 'flex-end' } }}>
            <Filters
              type={VocuherCategory.SALE}
              filterHandler={handleFilterChange}
              hideFilter={true}
              hidePartySearch={true}
              hideReset={true}
              showLocation={true}
            />
          </Box>
          <DashboardsMenu />
        </Stack>
      </Stack>

      {errorMessage && (
        <Paper sx={{ ...cardSx, p: 1.25, mb: 1.5, borderColor: '#fca5a5', color: '#b91c1c', fontSize: '0.8rem' }}>{errorMessage}</Paper>
      )}

      {/* KPI Row */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Revenue"
            value={fmtFull(totalRevenue)}
            icon={<ReceiptLongIcon />}
            accent={ACCENT}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Expenses"
            value={fmtFull(totalExpenses)}
            icon={<CreditCardIcon />}
            accent="#ef4444"
            sub={{ positive: false, label: 'COGS + OpEx + Other' }}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Net Profit"
            value={fmtFull(netProfit)}
            icon={<PaidIcon />}
            accent={netProfit >= 0 ? ACCENT : '#ef4444'}
            // sub={netMargin ? { positive: netProfit >= 0, label: `${netMargin.toFixed(1)}% margin` } : null}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Assets"
            value={fmtFull(totalAssets)}
            icon={<AccountBalanceIcon />}
            accent="#3b82f6"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard
            title="Total Liabilities"
            value={fmtFull(totalLiabilities)}
            icon={<SavingsIcon />}
            accent="#f59e0b"
            sub={{ positive: true, label: `Equity ${fmtFull(totalEquity)}` }}
          />
        </Grid>
      </Grid>

      {/* Row 1: P&L Flow + BS Composition */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>Profit & Loss Flow</Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.7rem' }}>From journal entries</Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> : (
              <Chart options={plBarOptions} series={plBarSeries} type="bar" height={260} />
            )}

            {/* Click a bar → the same breakdown as the hover tooltip, but FROZEN
                in place (floats over the chart, stays until you close it). */}
            {plDialog && (
              <Box sx={{
                position: 'absolute',
                // Anchor to the clicked bar. translateX(-50%) centers it over the
                // cursor; clamp keeps it inside the card on either edge.
                left: `clamp(150px, ${plDialog.x}px, calc(100% - 150px))`,
                top: 8,
                transform: 'translate(-50%, 0)',
                zIndex: 20,
                width: 300,
                maxWidth: 'calc(100% - 24px)',
                backgroundColor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${plDialog.color}`, backgroundColor: '#fafafb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: plDialog.color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: TEXT }} noWrap>{plDialog.title}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.82rem', color: plDialog.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtShort(plDialog.total)}</Typography>
                    <IconButton size="small" onClick={() => setPlDialog(null)} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </Stack>
                {(() => {
                  const ledgers = flattenLedgers(plDialog.groups);
                  if (ledgers.length === 0) {
                    return <Box sx={{ py: 2, textAlign: 'center', color: MUTED, fontSize: '0.78rem' }}>No ledger detail for this bucket.</Box>;
                  }
                  return (
                    <Box sx={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      // Always show a scrollbar track so it's obvious there's more to scroll.
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: BORDER, borderRadius: 4 },
                      scrollbarWidth: 'thin',
                    }}>
                      {ledgers.map((l: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderBottom: idx < ledgers.length - 1 ? '1px dashed #eef0f3' : 'none' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT }} noWrap>{l.name}</Typography>
                            {l.group && <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{l.group}</Typography>}
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: plDialog.color }}>{fmtShort(l.balance || l.debit || l.credit || 0)}</Typography>
                            {(l.debit > 0 || l.credit > 0) && (
                              <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>Dr {fmtShort(l.debit)} · Cr {fmtShort(l.credit)}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Ledger
              </Typography>
              <Autocomplete
                size="small"
                options={allLedgers}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(a, b) => a.key === b.key}
                value={selectedLedger ?? null}
                onChange={(_, val) => setSelectedLedgerKey(val ? val.key : null)}
                sx={{ minWidth: { xs: '100%', sm: 220 } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select ledger"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                        backgroundColor: SURFACE,
                        fontSize: '0.75rem',
                      },
                    }}
                  />
                )}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.key}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: TEXT }}>
                      {opt.name}
                    </Typography>
                  </li>
                )}
              />
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
            ) : allLedgers.length === 0 ? (
              <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>
                No ledger data
              </Box>
            ) : (
              <Chart options={ledgerBarOptions} series={ledgerBarSeries} type="bar" height={260} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2: Revenue & Expenses (full width) */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, overflow: 'visible', position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Revenue & Expenses
              </Typography>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ACCENT }} />
                  <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>Revenue</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  <Typography sx={{ color: MUTED, fontSize: '0.68rem' }}>Expense</Typography>
                </Stack>
              </Stack>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} /> :
              !hasCombinedData ? (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                <Chart options={combinedBarOpts} series={combinedBarSeries} type="bar" height={300} />
              )}

            {/* Click a bar → freeze its ledger-group breakdown next to it. */}
            {reDialog && (
              <Box sx={{
                position: 'absolute',
                left: `clamp(150px, ${reDialog.x}px, calc(100% - 150px))`,
                top: 8,
                transform: 'translate(-50%, 0)',
                zIndex: 20,
                width: 280,
                maxWidth: 'calc(100% - 24px)',
                backgroundColor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${reDialog.color}`, backgroundColor: '#fafafb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: reDialog.color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: TEXT }} noWrap>{reDialog.title}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.82rem', color: reDialog.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtShort(reDialog.total)}</Typography>
                    <IconButton size="small" onClick={() => setReDialog(null)} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </Stack>
                {(() => {
                  const ledgers = flattenLedgers(reDialog.groups);
                  if (ledgers.length === 0) {
                    return <Box sx={{ py: 2, textAlign: 'center', color: MUTED, fontSize: '0.78rem' }}>No ledger detail for this group.</Box>;
                  }
                  return (
                    <Box sx={{
                      maxHeight: 200, overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: BORDER, borderRadius: 4 },
                      scrollbarWidth: 'thin',
                    }}>
                      {ledgers.map((l: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderBottom: idx < ledgers.length - 1 ? '1px dashed #eef0f3' : 'none' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT }} noWrap>{l.name}</Typography>
                            {l.group && <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{l.group}</Typography>}
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: reDialog.color }}>{fmtShort(l.balance || l.debit || l.credit || 0)}</Typography>
                            {(l.debit > 0 || l.credit > 0) && (
                              <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>Dr {fmtShort(l.debit)} · Cr {fmtShort(l.credit)}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 3: Balance Sheet + Assets Breakdown + Liabilities Breakdown */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Balance Sheet
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: '0.7rem' }}>
                {fmtShort(totalAssets + totalLiabilities + totalEquity)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> :
              bsDonutSeries.every((v) => v === 0) ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                <Chart options={bsDonutOptions} series={bsDonutSeries} type="donut" height={260} />
              )}

            {/* Click a slice → freeze its ledger-group breakdown next to it. */}
            {bsDialog && (
              <Box sx={{
                position: 'absolute',
                left: `clamp(150px, ${bsDialog.x}px, calc(100% - 150px))`,
                top: 8,
                transform: 'translate(-50%, 0)',
                zIndex: 20,
                width: 280,
                maxWidth: 'calc(100% - 24px)',
                backgroundColor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${bsDialog.color}`, backgroundColor: '#fafafb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: bsDialog.color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: TEXT }} noWrap>{bsDialog.title}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.82rem', color: bsDialog.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtShort(bsDialog.total)}</Typography>
                    <IconButton size="small" onClick={() => setBsDialog(null)} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </Stack>
                {(() => {
                  const ledgers = flattenLedgers(bsDialog.groups);
                  if (ledgers.length === 0) {
                    return <Box sx={{ py: 2, textAlign: 'center', color: MUTED, fontSize: '0.78rem' }}>No ledger detail for this section.</Box>;
                  }
                  return (
                    <Box sx={{
                      maxHeight: 200, overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: BORDER, borderRadius: 4 },
                      scrollbarWidth: 'thin',
                    }}>
                      {ledgers.map((l: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderBottom: idx < ledgers.length - 1 ? '1px dashed #eef0f3' : 'none' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT }} noWrap>{l.name}</Typography>
                            {l.group && <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{l.group}</Typography>}
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: bsDialog.color }}>{fmtShort(l.balance || l.debit || l.credit || 0)}</Typography>
                            {(l.debit > 0 || l.credit > 0) && (
                              <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>Dr {fmtShort(l.debit)} · Cr {fmtShort(l.credit)}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Assets Breakdown
              </Typography>
              <Typography sx={{ color: '#3b82f6', fontSize: '0.78rem', fontWeight: 700 }}>
                {fmtShort(totalAssets)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> :
              assetsGroupsBreak.values.length === 0 ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                <Chart options={assetsDonutOptions} series={assetsGroupsBreak.values} type="donut" height={260} />
              )}

            {/* Click a slice → freeze that asset group's ledger breakdown next to it. */}
            {assetsDialog && (
              <Box sx={{
                position: 'absolute',
                left: `clamp(150px, ${assetsDialog.x}px, calc(100% - 150px))`,
                top: 8,
                transform: 'translate(-50%, 0)',
                zIndex: 20,
                width: 280,
                maxWidth: 'calc(100% - 24px)',
                backgroundColor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${assetsDialog.color}`, backgroundColor: '#fafafb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: assetsDialog.color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: TEXT }} noWrap>{assetsDialog.title}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.82rem', color: assetsDialog.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtShort(assetsDialog.total)}</Typography>
                    <IconButton size="small" onClick={() => setAssetsDialog(null)} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </Stack>
                {(() => {
                  const ledgers = flattenLedgers(assetsDialog.groups);
                  if (ledgers.length === 0) {
                    return <Box sx={{ py: 2, textAlign: 'center', color: MUTED, fontSize: '0.78rem' }}>No ledger detail for this group.</Box>;
                  }
                  return (
                    <Box sx={{
                      maxHeight: 200, overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: BORDER, borderRadius: 4 },
                      scrollbarWidth: 'thin',
                    }}>
                      {ledgers.map((l: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderBottom: idx < ledgers.length - 1 ? '1px dashed #eef0f3' : 'none' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT }} noWrap>{l.name}</Typography>
                            {l.group && <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{l.group}</Typography>}
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: assetsDialog.color }}>{fmtShort(l.balance || l.debit || l.credit || 0)}</Typography>
                            {(l.debit > 0 || l.credit > 0) && (
                              <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>Dr {fmtShort(l.debit)} · Cr {fmtShort(l.credit)}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Liabilities Breakdown
              </Typography>
              <Typography sx={{ color: '#f59e0b', fontSize: '0.78rem', fontWeight: 700 }}>
                {fmtShort(totalLiabilities)}
              </Typography>
            </Stack>
            {loading ? <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} /> :
              liabGroupsBreak.values.length === 0 ? (
                <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                <Chart options={liabDonutOptions} series={liabGroupsBreak.values} type="donut" height={260} />
              )}

            {/* Click a slice → freeze that liability group's ledger breakdown next to it. */}
            {liabDialog && (
              <Box sx={{
                position: 'absolute',
                left: `clamp(150px, ${liabDialog.x}px, calc(100% - 150px))`,
                top: 8,
                transform: 'translate(-50%, 0)',
                zIndex: 20,
                width: 280,
                maxWidth: 'calc(100% - 24px)',
                backgroundColor: '#fff',
                border: `1px solid ${BORDER}`,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${liabDialog.color}`, backgroundColor: '#fafafb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: liabDialog.color }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: TEXT }} noWrap>{liabDialog.title}</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 800, fontSize: '0.82rem', color: liabDialog.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtShort(liabDialog.total)}</Typography>
                    <IconButton size="small" onClick={() => setLiabDialog(null)} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </Stack>
                {(() => {
                  const ledgers = flattenLedgers(liabDialog.groups);
                  if (ledgers.length === 0) {
                    return <Box sx={{ py: 2, textAlign: 'center', color: MUTED, fontSize: '0.78rem' }}>No ledger detail for this group.</Box>;
                  }
                  return (
                    <Box sx={{
                      maxHeight: 200, overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 8 },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: BORDER, borderRadius: 4 },
                      scrollbarWidth: 'thin',
                    }}>
                      {ledgers.map((l: any, idx: number) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderBottom: idx < ledgers.length - 1 ? '1px dashed #eef0f3' : 'none' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT }} noWrap>{l.name}</Typography>
                            {l.group && <Typography sx={{ fontSize: '0.65rem', color: MUTED }} noWrap>{l.group}</Typography>}
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: liabDialog.color }}>{fmtShort(l.balance || l.debit || l.credit || 0)}</Typography>
                            {(l.debit > 0 || l.credit > 0) && (
                              <Typography sx={{ fontSize: '0.62rem', color: MUTED }}>Dr {fmtShort(l.debit)} · Cr {fmtShort(l.credit)}</Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

    </Box>
  );
};

export default CoaDashboard;
